import { Injectable } from '@angular/core';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import {
  BehaviorSubject,
  merge,
  forkJoin,
  EMPTY,
  Observable,
  of,
  throwError,
} from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';

import { IOrderSalesShipstationAddress } from '../../order/interfaces/order-picklist-preview';
import { IPicklistOrderShipment } from '../../picklist';
import {
  IWarehouse,
  LocationApiService,
  ShipstationApiService,
  SystemSettingApiService,
} from '../../shared';
import { SystemSettingInterface } from '../../system-setting';
import { ShippingLabelRatesComponent } from '../components/shipping-label-rates/shipping-label-rates.component';
import {
  IShippingCarrier,
  IShippingCarrierPackage,
  IShippingCarrierService,
  IShippingItem,
  IShippingLabelOrderLineItemDimension,
  IShipstationRates,
  IWeightUnit,
} from '../interfaces';

@Injectable({
  providedIn: 'root',
})
export class ShippingLabelFacade {
  warehouseAddress ?:IOrderSalesShipstationAddress | null;
  defaultDimensionUnits :{ label :string; value :string } = {
    label: 'in',
    value: 'inches',
  };

  private readonly _defaultWarehouse = new BehaviorSubject<IWarehouse | null>(null);

  private readonly _shippingCarriers = new BehaviorSubject<{
    [appIntegrationAccountIdString :string] :IShippingCarrier[];
  }>({});

  private readonly _shippingPackages = new BehaviorSubject<{
    [carrierCode :string] :IShippingCarrierPackage[];
  }>({});

  private readonly _shippingServices = new BehaviorSubject<{
    [carrierCode :string] :IShippingCarrierService[];
  }>({});

  constructor(
    private shipStationService :ShipstationApiService,
    private dialog :MatDialog,
    private snackBar :MatSnackBar,
    private systemSettingService :SystemSettingApiService,
    private locationService :LocationApiService,
  ) {
    this.getDefaultSystemWarehouse;
  }

  public createShippingLabel(payload) :Observable<IPicklistOrderShipment> {
    return this.shipStationService.createShippingLabel(payload);
  }

  public createBulkShippingLabel(payload) :Observable<IPicklistOrderShipment> {
    return this.shipStationService.bulkCreateShippingLabel(payload);
  }

  public getSortedRates(
    shippingItem :IShippingItem,
    showRates :boolean = false,
    warehouseAddress ?:IOrderSalesShipstationAddress,
    carriers ?:IShippingCarrier[],
  ) :Observable<IShipstationRates | null> {
    const rateParams = this.composerGetRateParams(shippingItem, warehouseAddress);
    if (!rateParams) {
      return of(null);
    }
    if (showRates && carriers && carriers.length) {
      const ratesObservables = carriers.map((carrier) => {
        const params = { ...rateParams, carrierCode: carrier.code };

        return this.shipStationService.getRates(params).pipe(
          map((rates) =>
            rates.map((rate) => ({
              ...rate,
              carrierCode: carrier.code,
              carrierName: carrier.name,
            })),
          ),
          catchError((error) => {
            return of([]);
          }),
        );
      });

      return forkJoin(ratesObservables).pipe(
        map((ratesArrays) => {
          const combinedRates = []
            .concat(...ratesArrays)
            .sort((a, b) => a.shipmentCost - b.shipmentCost);

          return combinedRates;
        }),
        switchMap((combinedRates) => {
          if (combinedRates.length === 0) {
            return of(null);
          }
          // TODO: This should be on component level
          const dialogRef = this.dialog.open(ShippingLabelRatesComponent, {
            width: '780px',
            data: combinedRates,
          });

          return dialogRef
            .afterClosed()
            .pipe(map((selectedRate) => selectedRate || null));
        }),
        catchError((error) => {
          return of(null);
        }),
      );
    } else {
      if (shippingItem.serviceCode) {
        rateParams.serviceCode = shippingItem.serviceCode;
      }
      if (shippingItem.packageCode) {
        rateParams.packageCode = shippingItem.packageCode;
      }
      if (!shippingItem.carrierCode) {
        return of(null);
      }

      return this.shipStationService.getRates(rateParams).pipe(
        switchMap((rates :IShipstationRates[]) => {
          const sortedRates = rates.sort((a, b) => a.shipmentCost - b.shipmentCost);
          if (sortedRates.length === 0) {
            return of(null); // No rates available
          } else {
            return of(sortedRates[0]);
          }
        }),
        catchError((e) => throwError(e)),
      );
    }
  }

  public loadCarriersData(appIntegrationAccountId ?:number) :Observable<any> {
    const key :string = appIntegrationAccountId?.toString() || '0';
    const carriersMap :{ [key :string] :IShippingCarrier[] } =
      this._shippingCarriers.value;
    if (carriersMap.hasOwnProperty(key)) {
      return of(carriersMap[key]);
    }
    const params :{ appIntegrationAccountId ?:string; limit ?:string } = {
      appIntegrationAccountId: appIntegrationAccountId?.toString() || '0',
      limit: '100',
    };

    return this.shipStationService.getCarrierByParams(params).pipe(
      tap((response) => {
        const { data } = response;
        carriersMap[key] = data;
        this._shippingCarriers.next(carriersMap);
      }),
      map((response) => response.data),
      catchError((error) => {
        console.error('Error fetching carriers:', error);

        return EMPTY;
      }),
    );
  }

  public loadPackages(
    carrierCode :string,
    salesOrderId :number = 0,
  ) :Observable<IShippingCarrierPackage[]> {
    const packagesMap :{ [carrierCode :string] :IShippingCarrierPackage[] } =
      this._shippingPackages.value;
    if (packagesMap.hasOwnProperty(carrierCode)) {
      return of(packagesMap[carrierCode]);
    }
    const params = this.getServicePackageParams(carrierCode, salesOrderId);

    return this.shipStationService.getPackagesByParams(params).pipe(
      tap((response) => {
        const { data } = response;
        packagesMap[carrierCode] = data;
        this._shippingPackages.next(packagesMap);
      }),
      map((response) => response.data),
      catchError((error) => {
        console.error('Error fetching packages:', error);

        return EMPTY;
      }),
    );
  }

  public loadServices(
    carrierCode :string,
    salesOrderId :number = 0,
  ) :Observable<IShippingCarrierService[]> {
    const servicesMap :{ [carrierCode :string] :IShippingCarrierService[] } =
      this._shippingServices.value;
    if (servicesMap.hasOwnProperty(carrierCode)) {
      return of(servicesMap[carrierCode]);
    }
    const params = this.getServicePackageParams(carrierCode, salesOrderId);

    return this.shipStationService.getServicesByParams(params).pipe(
      tap((response) => {
        const { data } = response;
        servicesMap[carrierCode] = data;
        this._shippingServices.next(servicesMap);
      }),
      map((response) => response.data),
      catchError((error) => {
        console.error('Error fetching services:', error);

        return EMPTY;
      }),
    );
  }

  public getDefaultSystemWarehouse() :Observable<IWarehouse | null> {
    return this.systemSettingService.getInventorySettingsAsyc().pipe(
      switchMap((inventorySettings :SystemSettingInterface) => {
        if (inventorySettings.value) {
          const invSettingsValue = JSON.parse(inventorySettings.value);
          if (invSettingsValue.warehouse_id) {
            return this.getWarehouseById(invSettingsValue.warehouse_id);
          }
        }

        return EMPTY; // Return null or EMPTY observable if condition not met
      }),
      catchError((error) => {
        console.error(error);

        return EMPTY; // Handle errors or return EMPTY observable
      }),
    );
  }

  public getConfirmationOptions(carrierCode ?:string) :string[] {
    let options = ['none', 'delivery', 'signature', 'adult_signature'];

    if (carrierCode && carrierCode === 'fedex') {
      options.push('direct_signature');
    } else {
      if (options.includes('direct_signature')) {
        options = options.filter((option) => option !== 'direct_signature');
      }
    }

    return options;
  }

  public getAddressText(address :IOrderSalesShipstationAddress) :string {
    return this.shipStationService.getShipstationAddressText(address);
  }

  public setWarehouseAddress(
    warehouseAddress :IOrderSalesShipstationAddress,
  ) :void {
    this.warehouseAddress = warehouseAddress;
  }

  private composerGetRateParams(
    shippingItem :IShippingItem,
    warehouseAddress ?:IOrderSalesShipstationAddress,
  ) :any {
    if (!warehouseAddress) {
      warehouseAddress = this.warehouseAddress;
    }
    const weightValue :IWeightUnit = {} as IWeightUnit;
    if (shippingItem.weight.pounds.value > 0) {
      weightValue.units = shippingItem.weight.pounds.units;
      weightValue.value = shippingItem.weight.pounds.value;
    } else if (
      shippingItem.weight.pounds.value === 0 &&
      shippingItem.weight.ounces.value > 0
    ) {
      weightValue.units = shippingItem.weight.ounces.units;
      weightValue.value = shippingItem.weight.ounces.value;
    }
    if (!weightValue.units) {
      return false;
    }

    const postalCode =
      shippingItem.shipTo?.postalCode || shippingItem.shipTo?.postal_code;
    const params :any = {
      carrierCode: shippingItem.carrierCode,
      weight: weightValue,
      fromPostalCode: warehouseAddress?.postalCode,
      toPostalCode: postalCode,
      toCountry: shippingItem.shipTo?.country,
      toCity: shippingItem.shipTo?.city,
      toState: shippingItem.shipTo?.state,
      salesOrderId: shippingItem.salesOrderId ?? null,
    };

    const dimensions :IShippingLabelOrderLineItemDimension =
      shippingItem.dimensions;
    if (
      dimensions.height &&
      dimensions.length &&
      dimensions.width &&
      dimensions.units
    ) {
      params.dimensions = dimensions;
    }

    return params;
  }

  private getServicePackageParams(
    carrierCode :string,
    salesOrderId :number = 0,
    appIntegrationAccountId :number = 0,
  ) {
    const params :{
      appIntegrationAccountId ?:string;
      carrierCode ?:string;
      salesOrderId ?:string;
      limit ?:string;
    } = {
      limit: '100',
      carrierCode: carrierCode,
    };
    if (salesOrderId > 0) {
      params.salesOrderId = salesOrderId.toString();
    }
    if (appIntegrationAccountId > 0) {
      params.appIntegrationAccountId = appIntegrationAccountId.toString();
    }

    return params;
  }

  private getWarehouseById(warehouseId :number) :Observable<IWarehouse | null> {
    return this.locationService.getById(warehouseId).pipe(
      switchMap((warehouse :IWarehouse) => {
        if (warehouse && warehouse.id) {
          this._defaultWarehouse.next(warehouse); // Assuming this is intended to update some BehaviorSubject state

          return of(warehouse);
        }

        return EMPTY;
      }),
      catchError((error) => {
        console.error(error);

        return EMPTY; // Handle errors
      }),
    );
  }

  // GETTERS
  get shippingServices$() :Observable<{
    [carrierCode :string] :IShippingCarrierService[];
  }> {
    return this._shippingServices.asObservable();
  }

  get shippingPackages$() :Observable<{
    [carrierCode :string] :IShippingCarrierPackage[];
  }> {
    return this._shippingPackages.asObservable();
  }

  get shippingCarriers$() :Observable<{
    [appIntegrationAccountIdString :string] :IShippingCarrier[];
  }> {
    return this._shippingCarriers.asObservable();
  }

  get defaultWarehouse$() :Observable<IWarehouse | null> {
    return this._defaultWarehouse.asObservable();
  }
}
